
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

export const useBulkUpdates = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedUpdates, setSelectedUpdates] = useState<number[]>([]);

  const handleCheckboxChange = (checked: boolean, id: number) => {
    setSelectedUpdates(prev => 
      checked 
        ? [...prev, id]
        : prev.filter(updateId => updateId !== id)
    );
  };

  const handleBulkFrequencyUpdate = async (newFrequency: string) => {
    if (selectedUpdates.length === 0) {
      toast({
        title: "No Updates Selected",
        description: "Please select updates to modify",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('automated_updates')
        .update({ update_frequency: newFrequency })
        .in('id', selectedUpdates);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Updated frequency for ${selectedUpdates.length} items`,
      });

      await queryClient.invalidateQueries({ queryKey: ['automated-updates'] });
    } catch (error) {
      console.error('Bulk update error:', error);
      toast({
        title: "Error",
        description: "Failed to update frequencies",
        variant: "destructive",
      });
    }
  };

  const handleDeleteSelected = async (deleteMutation: any) => {
    if (selectedUpdates.length === 0) {
      toast({
        title: "No Updates Selected",
        description: "Please select updates to delete",
        variant: "destructive",
      });
      return;
    }

    await deleteMutation.mutate(selectedUpdates);
    setSelectedUpdates([]);
  };

  return {
    selectedUpdates,
    setSelectedUpdates,
    handleCheckboxChange,
    handleBulkFrequencyUpdate,
    handleDeleteSelected
  };
};
